import { Controller } from '@hotwired/stimulus'
import ApexCharts from 'apexcharts'
import { stimulus } from '~/init'

// Renders an ApexCharts bar or donut from data passed via Stimulus values.
// Usage:
//   <div data-controller="chart"
//        data-chart-type-value="bar"
//        data-chart-series-value="[80,197,...]"
//        data-chart-labels-value='["Jun 10",...]'></div>
export default class ChartController extends Controller {
  static values = {
    type: { type: String, default: 'bar' },
    series: Array,
    labels: Array,
    colors: Array,
    height: { type: Number, default: 300 }
  }

  connect () {
    this.chart = new ApexCharts(this.element, this.#options())
    this.chart.render()
  }

  disconnect () {
    this.chart?.destroy()
  }

  #options () {
    return this.typeValue === 'donut' ? this.#donutOptions() : this.#barOptions()
  }

  #barOptions () {
    return {
      chart: { type: 'bar', height: this.heightValue, toolbar: { show: false }, fontFamily: 'inherit' },
      series: [{ name: 'Orders', data: this.seriesValue }],
      xaxis: {
        categories: this.labelsValue,
        tickPlacement: 'on',
        // Weekly buckets mean few bars; show every week's range label, rotated
        // so the "Jun 22 – Jun 28" ranges don't collide.
        labels: {
          hideOverlappingLabels: false,
          rotate: -45,
          rotateAlways: true,
          trim: false,
          style: { fontSize: '11px' }
        }
      },
      colors: this.#palette(),
      states: this.#states(),
      plotOptions: { bar: { borderRadius: 6, columnWidth: '45%' } },
      dataLabels: { enabled: false },
      grid: { borderColor: 'rgba(0,0,0,0.06)' },
      tooltip: { theme: 'light' }
    }
  }

  #donutOptions () {
    return {
      chart: { type: 'donut', height: this.heightValue, fontFamily: 'inherit' },
      series: this.seriesValue,
      labels: this.labelsValue,
      colors: this.#palette(),
      states: this.#states(),
      legend: { position: 'bottom' },
      dataLabels: { enabled: true, formatter: (val) => `${Math.round(val)}%` },
      stroke: { width: 0 }
    }
  }

  // Hover lightens a segment/bar to signal it's interactive; active (click)
  // darkens it so the selected status stays visually pinned once picked.
  #states () {
    return {
      hover: { filter: { type: 'lighten', value: 0.02 } },
      active: { allowMultipleDataPointsSelection: false, filter: { type: 'darken', value: 0.02 } }
    }
  }

  #palette () {
    if (this.hasColorsValue && this.colorsValue.length) return this.colorsValue
    return ['#5F9A90', '#ffe4a0', '#B4D0BA', '#D8A48F', '#C97B63', '#E0C36B', '#9AA7B2', '#7C8B99', '#B0645A']
  }
}

stimulus.register('chart', ChartController)
